// Riel de modulos (D26): la columna angosta de la izquierda con los modulos (Inicio, Bandeja, Clientes, Ventas,
// Reportes; Administracion abajo) y su numero de pendientes, y al final el menu de la persona (estado, perfil,
// telefono, salir). Las secciones de cada modulo viven en el panel de al lado (PanelModulo).
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';

import { useSession } from '../auth/session';
import { usePhone } from '../hooks';
import { empresaDeWorkspace, empresaDelHost, urlConfiguracionAvanzada } from '../lib/direcciones';
import { Icon } from '../lib/icons';
import { phone, PRESENCIAS, type Presencia } from '../phone/store';
import { Avatar } from '../ui/bits';
import { toast } from '../ui/toasts';
import type { Modulo } from './menu';
import { cuentaDeModulo, destinoDeModulo, useContadores, useNavegacion } from './navegacion';

// La configuracion avanzada (panel tecnico del motor) es solo para el equipo Xmartek.
const EMPRESA_XMARTEK = import.meta.env.VITE_EMPRESA_XMARTEK || 'xmartek';
const esXmartek = (url?: string) => !!url && empresaDeWorkspace(url) === EMPRESA_XMARTEK;

export function Riel() {
  const { session, yo, salir, esAdmin } = useSession();
  const s = usePhone();
  const nav = useNavigate();
  const { modulos, actual } = useNavegacion();
  const contadores = useContadores();
  const [menu, setMenu] = useState<DOMRect | null>(null);

  const salirDe = (que: string) => {
    setMenu(null);
    if (s.call) return toast(`Termina la llamada antes de ${que}.`, 'info');
    phone.disconnect();
    salir();
  };
  const ir = (r: string) => (setMenu(null), nav(r));

  const boton = (m: Modulo) => {
    const c = cuentaDeModulo(m, contadores);
    const activo = actual?.modulo.id === m.id;
    return (
      <Link
        key={m.id}
        to={destinoDeModulo(m)}
        className="mod"
        aria-current={activo ? 'page' : undefined}
        aria-label={c.n ? `${m.nombre}, ${c.n} pendientes` : m.nombre}
        data-testid={`mod-${m.id}`}
        // Novedad (Balizas): fuera de Reportes, la baliza de "Llamadas 3CX" va en el boton del modulo.
        data-baliza={!activo && m.secciones.some((s) => s.ruta === '/reportes/3cx') ? 'reportes-3cx' : undefined}
      >
        <Icon name={m.icono} />
        <span className="t">{m.id === 'admin' ? 'Admin' : m.nombre}</span>
        {c.n ? <span className={`b ${c.tono ?? ''}`}>{c.n > 99 ? '99+' : c.n}</span> : null}
      </Link>
    );
  };

  return (
    <nav className="riel" aria-label="Módulos">
      <Link to="/hoy" className="riel-marca" aria-label={`CRM XmarteK · ${session.workspace.nombre}`} title={session.workspace.nombre}>
        <Icon name="wave" />
      </Link>
      <div className="riel-mods">{modulos.filter((m) => !m.abajo).map(boton)}</div>
      <div className="riel-abajo">
        {modulos.filter((m) => m.abajo).map(boton)}
        <button className="riel-yo" onClick={(e) => setMenu(e.currentTarget.getBoundingClientRect())} aria-haspopup="menu" aria-label={`${yo.nombre} · ${PRESENCIAS[s.presencia].nombre}. Tu perfil y opciones`}>
          <Avatar nombre={yo.nombre} size="sm" />
          <span className={`idot ${s.presencia}`} />
        </button>
      </div>

      {menu
        ? createPortal(
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 69 }} onClick={() => setMenu(null)} />
              <div
                className="menu"
                role="menu"
                // Escritorio: al lado del avatar. Celular (riel abajo): encima de la barra.
                style={window.innerWidth > 900 ? { bottom: Math.max(8, window.innerHeight - menu.bottom), left: menu.right + 8 } : { bottom: window.innerHeight - menu.top + 6, right: 8 }}
              >
                <div className="mh">
                  <div style={{ fontWeight: 600 }}>{yo.nombre}</div>
                  <div className="note">{session.modo === 'usuario' ? yo.email : 'Conectado con API key'}</div>
                  <div className="note">{session.workspace.nombre}</div>
                </div>
                <hr />
                <p className="lbl" style={{ padding: '4px 10px 0' }}>
                  Tu estado
                </p>
                <div className="menu-estados" role="group" aria-label="Tu estado">
                  {(Object.keys(PRESENCIAS) as Presencia[]).map((p) => (
                    <button key={p} role="menuitemradio" aria-checked={s.presencia === p} onClick={() => (phone.setPresencia(p), setMenu(null))}>
                      <span className={`idot ${p}`} /> {PRESENCIAS[p].nombre}
                    </button>
                  ))}
                </div>
                <hr />
                <button role="menuitem" onClick={() => ir('/perfil')}>
                  <Icon name="user" /> Mi perfil
                </button>
                <button role="menuitem" onClick={() => (setMenu(null), phone.expand('ajustes'))}>
                  <Icon name="phone" /> Configurar teléfono
                </button>
                {esAdmin && esXmartek(session.workspace.url) ? (
                  <a role="menuitem" href={urlConfiguracionAvanzada(session.workspace.url!)} target="_blank" rel="noreferrer" onClick={() => setMenu(null)}>
                    <Icon name="external" /> Configuración avanzada
                  </a>
                ) : null}
                <hr />
                {!empresaDelHost() ? (
                  <button role="menuitem" onClick={() => salirDe('cambiar de empresa')}>
                    <Icon name="swap" /> Cambiar de empresa
                  </button>
                ) : null}
                <button role="menuitem" onClick={() => salirDe('salir')}>
                  <Icon name="logout" /> Salir
                </button>
              </div>
            </>,
            document.body,
          )
        : null}
    </nav>
  );
}
